import {useMutation} from '@tanstack/react-query';
import {useNavigate} from 'react-router';

import {useExperimentsStore} from 'apps/experiments/src/store';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {getReportUrl} from 'common/report/util';
import {useExperimentsApi} from 'apps/experiments/src/api/experiments';
import {useTranslation} from 'common/i18n/hooks';
import {SidenavSectionIndex, SidenavSectionTrackingName} from 'common/widgets/content-sidenav/constants';
import {useContentSidenavContext} from 'common/widgets/content-sidenav/context';

import {PageTrackingEvent, trackExperimentPageAction, TrackingEventSource} from '../tracking';
import {useExperimentPageContext} from '../context';
import {useStagedExperiment} from './staged-experiment';

type DuplicateExperimentParams = {
  isListPage?: boolean;
};

export function useDuplicateExperiment({isListPage}: DuplicateExperimentParams) {
  const {projectId, workspaceId} = useExperimentPageContext();
  const contentSidenavContext = useContentSidenavContext();
  const {stagedExperiment: experiment} = useStagedExperiment();
  const experiments = useExperimentsStore((state) => state.experiments);
  const setExperiments = useExperimentsStore((state) => state.setExperiments);
  const {t} = useTranslation();
  const navigate = useNavigate();

  const {duplicateExperiment} = useExperimentsApi({
    projectId,
    workspaceId,
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicateExperiment,
    onSuccess: (data) => {
      contentSidenavContext?.insertContentSidenavItem(
        data,
        SidenavSectionIndex.Yours,
        SidenavSectionTrackingName.Yours,
      );
      addToast({
        message: t(`experiments.actions.duplicate.success`),
        type: ToastType.SUCCESS,
        timeout: 6000,
      });
      if (isListPage) {
        setExperiments([...(experiments ?? []), data]);
      } else {
        navigate(
          getReportUrl(`experiments/${data.id.toString()}`, {
            projectId,
            workspaceId,
          }),
        );
      }
    },
    onError: () => {
      addToast({
        message: t(`experiments.actions.duplicate.fail`),
        type: ToastType.ERROR,
        timeout: 6000,
      });
    },
    onSettled: (_, error) => {
      trackExperimentPageAction({
        event: error ? PageTrackingEvent.DuplicateExperimentError : PageTrackingEvent.DuplicateExperiment,
        experiment: isListPage ? undefined : experiment,
        source: isListPage ? TrackingEventSource.ListPage : TrackingEventSource.ExperimentPage,
        props: error ? {'error message': error.message} : {},
      });
    },
  });

  return duplicateMutation;
}
